/**
 * "Send message" on a user's screen — the `user.detail.actions` slot.
 *
 * Shown only with `message.send` (the slot filters on the manifest's `anyOf`),
 * and not at all for yourself or for a deactivated account: the server would
 * refuse both, and a button whose only outcome is an error is worse than none.
 */
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import MailOutlinedIcon from '@mui/icons-material/MailOutlined';
import Button from '@mui/material/Button';
import type { SlotProps } from '../types';
import { useAuth } from '../../auth/useAuth';
import { ComposeDialog } from './ComposeDialog';

export function SendMessageButton({ user }: SlotProps['user.detail.actions']) {
  const { t } = useTranslation('messaging');
  const { user: self } = useAuth();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);

  if (!user.isActive || self?.id === user.id) return null;

  return (
    <>
      <Button variant="outlined" startIcon={<MailOutlinedIcon />} onClick={() => setOpen(true)}>
        {t('sendToUser')}
      </Button>
      <ComposeDialog
        open={open}
        initialRecipients={[{ type: 'user', id: user.id, displayName: user.displayName }]}
        onClose={() => setOpen(false)}
        onSent={(message) => navigate(`/messages/${message.id}`)}
      />
    </>
  );
}
